import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import AudioPlayer from '../components/AudioPlayer';
import ImageViewer from '../components/ImageViewer';
import { useToast } from '../components/ToastContainer';
import { exportResponseToPDF, getPdfOptionsWithPngLogo } from '../utils/pdfExport';
import { debounce } from '../utils/debounce';
import { useAuth } from '../contexts/AuthContext';

interface Answer {
  id: number;
  question_id: number;
  option_id?: number;
  selected_option_ids?: number[];
  selected_options?: Array<{ id: number; text: string; is_correct: boolean }>;
  text_answer?: string;
  audio_answer?: string;
  question_text: string;
  question_type: string;
  selected_option_text?: string;
  is_correct?: boolean | 'partial';
  is_gradable?: boolean;
  text_answer_is_correct?: boolean;
  correct_text_answer?: string;
  requires_correct_answer?: boolean;
  allow_multiple_answers?: boolean;
  score_percentage?: number;
  rating?: number; // 1=Wrong, 2=Partially correct, 3=Correct
  admin_comment?: string;
  rated_at?: string;
  rated_by?: string;
  question_image_file?: string; // Single image attached to question
  question_images?: Array<{ image_file: string; caption?: string; order_num: number }>; // Multiple images per question
  question_audio_file?: string; // Audio file attached to question
}

interface Scoring {
  total_questions: number;
  gradable_questions: number;
  correct_answers: number;
  incorrect_answers: number;
  // New integer counts (backend >= this change). Optional for back-compat
  correct_count?: number;
  partial_count?: number;
  incorrect_count?: number;
  non_gradable_questions: number;
  score_percentage: number | null;
  total_score_percentage: number | null;
  score_display: string;
}

interface Response {
  id: number;
  participant_name: string;
  participant_email: string;
  trainee_name?: string | null; // For evaluations: Q1 answer (trainee name)
  started_at: string;
  completed_at: string;
  answers?: Answer[];
  scoring?: Scoring;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  category?: string;
  quiz_type?: 'quiz' | 'evaluation' | 'assessment' | 'survey';
}

const QuizResponsesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { isAdmin, isSupervisor } = useAuth();
  
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [responses, setResponses] = useState<Response[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Initialize searchQuery from URL params
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(parseInt(searchParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalResponses, setTotalResponses] = useState<number>(0);
  const [pageSize] = useState<number>(20);
  const [ratingState, setRatingState] = useState<{[answerId: number]: {rating?: number, comment: string}}>({});
  const [savingComments, setSavingComments] = useState<{[answerId: number]: boolean}>({});
  // UI-only lock/edit state per answer. If an answer already has rating, it's locked by default.
  const [editingAnswers, setEditingAnswers] = useState<{[answerId: number]: boolean}>({});
  const debouncedSaveRefs = useRef<{[answerId: number]: (...args: any[]) => void}>({});
  const [answerFilter, setAnswerFilter] = useState<'correct' | 'partial' | 'incorrect' | null>(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  
  // AI suggestion state
  const [aiSuggestions, setAiSuggestions] = useState<{[answerId: number]: {
    similarity_score: number;
    similarity_percentage: number;
    suggested_rating: number;
    rating_label: string;
    confidence: string;
    ml_evaluation_id?: number;  // For tracking feedback
    loading?: boolean;
    error?: string;
  }}>({});
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState<{[answerId: number]: boolean}>({});
  
  // Get responseId from URL params
  const responseIdFromUrl = searchParams.get('responseId');
  
  // Separate function to load details (with option to update URL or not)
  const loadResponseDetails = useCallback(async (responseId: number, participantName: string, updateUrl: boolean = true) => {
    try {
      setLoadingDetails(true);
      const response = await api.get(`/api/quizzes/responses/${responseId}`);
      setSelectedResponse(response.data);
      setAnswerFilter(null); // Reset filter when selecting a new response
      // Initialize editing map: rated answers are locked (not editing) by default
      const initEditing: {[k: number]: boolean} = {};
      (response.data.answers || []).forEach((ans: Answer) => {
        if (ans.rating) {
          initEditing[ans.id] = false;
        }
      });
      setEditingAnswers(initEditing);
      
      // Update URL only if requested (not when syncing from URL)
      if (updateUrl) {
        const params = new URLSearchParams(searchParams);
        params.set('responseId', responseId.toString());
        setSearchParams(params, { replace: false });
      }
    } catch (err: any) {
      console.error('Error fetching response details:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch response details';
      showToast(errorMessage, 'error');
    } finally {
      setLoadingDetails(false);
    }
  }, [searchParams, setSearchParams, showToast]);
  
  useEffect(() => {
    // Initialize from URL params
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const initialSearch = searchParams.get('search') || '';
    setCurrentPage(initialPage);
    setSearchQuery(initialSearch);
    fetchData(initialPage, initialSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  // Track if this is the initial mount
  const isInitialMount = useRef(true);
  // Track previous searchParams to detect browser navigation
  const prevSearchParamsRef = useRef<string>(searchParams.toString());

  // Sync state FROM URL params when they change (browser back/forward)
  useEffect(() => {
    const currentParamsString = searchParams.toString();
    const prevParamsString = prevSearchParamsRef.current;
    
    // Only sync if URL params actually changed (browser navigation)
    if (currentParamsString !== prevParamsString) {
      prevSearchParamsRef.current = currentParamsString;
      
      const urlSearch = searchParams.get('search') || '';
      const urlPage = parseInt(searchParams.get('page') || '1', 10);
      const urlResponseId = searchParams.get('responseId');
      
      // Sync search query
      if (urlSearch !== searchQuery) {
        setSearchQuery(urlSearch);
      }
      
      // Sync page number
      if (urlPage !== currentPage) {
        setCurrentPage(urlPage);
      }
      
      // Fetch data if page or search changed
      if (urlPage !== currentPage || urlSearch !== searchQuery) {
        fetchData(urlPage, urlSearch);
      }
      
      // Sync response details
      if (urlResponseId) {
        const responseId = parseInt(urlResponseId, 10);
        if (!isNaN(responseId)) {
          // Only load if we don't already have this response selected
          if (!selectedResponse || selectedResponse.id !== responseId) {
            // Find the response to get participant name
            const response = responses.find(r => r.id === responseId);
            if (response) {
              // Load details without updating URL (to avoid creating another history entry)
              loadResponseDetails(responseId, response.participant_name, false);
            }
          }
        }
      } else {
        // If no responseId in URL, close details view (but don't update URL to avoid loop)
        if (selectedResponse) {
          setSelectedResponse(null);
          setAnswerFilter(null); // Reset filter when closing response
          setRatingState({});
          setEditingAnswers({});
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, responses, loadResponseDetails]);

  // Sync URL params when state changes (user actions)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSearchParamsRef.current = searchParams.toString();
      return;
    }

    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (currentPage > 1) params.set('page', currentPage.toString());
    // Preserve responseId if it exists (it's managed by loadResponseDetails)
    if (selectedResponse) {
      params.set('responseId', selectedResponse.id.toString());
    }
    
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    
    // Only update if params actually changed
    if (currentParamsString !== newParamsString) {
      prevSearchParamsRef.current = newParamsString;
      setSearchParams(params, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentPage, selectedResponse]);
  
  const fetchData = async (page: number = currentPage, search: string = searchQuery) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch quiz details (only on initial load)
      if (!quiz) {
        const quizResponse = await api.get(`/api/quizzes/${id}`);
        setQuiz(quizResponse.data);
      }
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Fetch quiz responses with pagination
      const responsesResponse = await api.get(`/api/quizzes/${id}/responses?${params.toString()}`);
      const responsesData = responsesResponse.data;
      
      // Handle new paginated response format: { responses: [...], pagination: {...} }
      // or backward compatibility with old format: [...]
      if (Array.isArray(responsesData)) {
        // Old format (backward compatibility) - load all
        setResponses(responsesData);
        setTotalResponses(responsesData.length);
        setTotalPages(1);
        setCurrentPage(1);
      } else if (responsesData && Array.isArray(responsesData.responses)) {
        // New paginated format
        setResponses(responsesData.responses);
        if (responsesData.pagination) {
          setCurrentPage(responsesData.pagination.currentPage);
          setTotalPages(responsesData.pagination.totalPages);
          setTotalResponses(responsesData.pagination.total);
        }
      } else {
        // Fallback to empty array if format is unexpected
        setResponses([]);
        setTotalResponses(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
      
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch data';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };
  
  const viewResponseDetails = async (responseId: number, participantName: string) => {
    // Use the shared function with URL update enabled
    await loadResponseDetails(responseId, participantName, true);
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const calculateDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };
  
  const closeResponseDetails = () => {
    setSelectedResponse(null);
    setAnswerFilter(null); // Reset filter when closing response
    setRatingState({});
    setEditingAnswers({});
    // Remove responseId from URL (creates history entry)
    const params = new URLSearchParams(searchParams);
    params.delete('responseId');
    setSearchParams(params, { replace: false });
  };
  
  const updateAnswerRating = async (answerId: number, rating?: number, comment?: string, mlEvaluationId?: number, aiSuggestionAccepted?: boolean) => {
    try {
      const requestBody: any = {};
      if (rating !== undefined) requestBody.rating = rating;
      if (comment !== undefined) requestBody.adminComment = comment;
      if (mlEvaluationId !== undefined) requestBody.ml_evaluation_id = mlEvaluationId;
      if (aiSuggestionAccepted !== undefined) requestBody.ai_suggestion_accepted = aiSuggestionAccepted;
      
      const response = await api.put(`/api/quizzes/answers/${answerId}/rating`, requestBody);
      
      // Update the answer in the current response
      if (selectedResponse && selectedResponse.answers) {
        const updatedAnswers = selectedResponse.answers.map(answer => {
          if (answer.id === answerId) {
            const updated = { ...answer };
            if (rating !== undefined) {
              updated.rating = rating;
              updated.rated_at = new Date().toISOString();
            }
            if (comment !== undefined) {
              updated.admin_comment = comment;
            }
            return updated;
          }
          return answer;
        });
        setSelectedResponse({ ...selectedResponse, answers: updatedAnswers });
        
        // Only recalculate scoring if rating was updated
        if (rating !== undefined) {
          await viewResponseDetails(selectedResponse.id, selectedResponse.participant_name);
        }
      }
      
      if (rating !== undefined) {
        // After first successful rating, leave edit mode (locking UI) unless the user re-enters edit
        setEditingAnswers(prev => ({ ...prev, [answerId]: false }));
      }
    } catch (err: any) {
      console.error('Error updating answer:', err);
      showToast(err.response?.data?.message || 'Failed to update', 'error');
    }
  };

  // Auto-save comment after user stops typing
  const saveComment = useCallback(async (answerId: number, comment: string) => {
    // If locked (has rating and not in edit mode), do not auto-save
    const answer = selectedResponse?.answers?.find(a => a.id === answerId);
    if (answer && answer.rating && !editingAnswers[answerId]) {
      return;
    }
    setSavingComments(prev => ({ ...prev, [answerId]: true }));
    try {
      await updateAnswerRating(answerId, undefined, comment);
      // Silent save - no toast for auto-save
    } catch (error) {
      console.error('Failed to auto-save comment:', error);
    } finally {
      setSavingComments(prev => ({ ...prev, [answerId]: false }));
    }
  }, [selectedResponse, editingAnswers]);

  // Get or create debounced save function for specific answer
  const getDebouncedSave = (answerId: number) => {
    if (!debouncedSaveRefs.current[answerId]) {
      debouncedSaveRefs.current[answerId] = debounce(
        (comment: string) => saveComment(answerId, comment),
        1000 // Save after 1 second of inactivity
      );
    }
    return debouncedSaveRefs.current[answerId];
  };

  // Fetch AI-suggested score for an answer
  const fetchAiSuggestion = async (answerId: number) => {
    setLoadingAiSuggestion(prev => ({ ...prev, [answerId]: true }));
    try {
      const response = await api.get(`/api/quizzes/answers/${answerId}/suggest-score`);
      setAiSuggestions(prev => ({
        ...prev,
        [answerId]: {
          similarity_score: response.data.similarity_score,
          similarity_percentage: response.data.similarity_percentage,
          suggested_rating: response.data.suggested_rating,
          rating_label: response.data.rating_label,
          confidence: response.data.confidence,
          ml_evaluation_id: response.data.ml_evaluation_id  // Store for feedback tracking
        }
      }));
    } catch (err: any) {
      console.error('Error fetching AI suggestion:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get AI suggestion';
      setAiSuggestions(prev => ({
        ...prev,
        [answerId]: {
          ...prev[answerId],
          error: errorMessage,
          similarity_score: 0,
          similarity_percentage: 0,
          suggested_rating: 0,
          rating_label: '',
          confidence: ''
        }
      }));
      showToast(errorMessage, 'error');
    } finally {
      setLoadingAiSuggestion(prev => ({ ...prev, [answerId]: false }));
    }
  };

  // Apply AI suggestion as the rating
  const applyAiSuggestion = (answerId: number) => {
    const suggestion = aiSuggestions[answerId];
    if (suggestion && suggestion.suggested_rating) {
      const comment = ratingState[answerId]?.comment || '';
      setRatingState(prev => ({
        ...prev,
        [answerId]: { ...prev[answerId], rating: suggestion.suggested_rating, comment }
      }));
      // Pass ml_evaluation_id and mark as accepted
      updateAnswerRating(answerId, suggestion.suggested_rating, comment, suggestion.ml_evaluation_id, true);
    }
  };

  const handleRefresh = () => {
    fetchData(currentPage, searchQuery);
    showToast('Refreshing responses...', 'info');
  };

  // Pagination handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchData(page, searchQuery);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchData(newPage, searchQuery);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchData(newPage, searchQuery);
    }
  };

  const firstPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      fetchData(1, searchQuery);
    }
  };

  const lastPage = () => {
    if (currentPage !== totalPages) {
      setCurrentPage(totalPages);
      fetchData(totalPages, searchQuery);
    }
  };

  // Debounced search handler using useRef
  const debouncedSearchRef = useRef(
    debounce((value: string) => {
      setCurrentPage(1); // Reset to first page on search
      fetchData(1, value);
    }, 500)
  );

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearchRef.current(value);
  };

  const exportResponses = () => {
    if (responses.length === 0) {
      showToast('No responses to export', 'warning');
      return;
    }

    const csvContent = [
      ['ID', 'Name', quiz?.quiz_type === 'evaluation' ? 'Trainee' : 'Email', 'Started', 'Completed', 'Duration', 'Score', 'Status'].join(','),
      ...responses.map(response => [
        response.id,
        `"${response.participant_name}"`,
        quiz?.quiz_type === 'evaluation' ? (response.trainee_name || 'N/A') : response.participant_email,
        formatDate(response.started_at),
        formatDate(response.completed_at),
        calculateDuration(response.started_at, response.completed_at),
        response.completed_at && response.scoring ? 
          (response.scoring.score_percentage !== null ? `${response.scoring.score_percentage}%` : 'N/A') : 
          '—',
        response.completed_at ? 'Completed' : 'In Progress'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-${id}-responses.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showToast('Responses exported successfully', 'success');
  };
  
  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <span className="loading-spinner"></span>
            Loading responses...
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !quiz) {
    return (
      <div className="page">
        <div className="container">
          <div className="alert alert-error">
            <strong>Error:</strong> {error || 'Quiz not found'}
            <button 
              onClick={handleRefresh}
              className="btn btn-small btn-outline mt-2"
            >
              Try Again
            </button>
          </div>
          <button 
            className="btn btn-outline"
            onClick={() => navigate('/admin')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="page-title">
            {quiz.quiz_type === 'evaluation' ? 'Evaluation Responses:' : 'Responses:'} {quiz.title}
          </h1>
          <div className="d-flex gap-2">
            {/* Answer Key button - only for supervisors and admins, and only for quizzes (not evaluations) */}
            {(isAdmin || isSupervisor) && quiz && (!quiz.quiz_type || quiz.quiz_type === 'quiz') && (
              <button 
                className="btn btn-small"
                onClick={() => navigate(`/admin/quiz/${id}/answer-key`)}
                style={{ 
                  backgroundColor: '#4CAF50', 
                  borderColor: '#4CAF50', 
                  color: 'white' 
                }}
                title="View answer key"
              >
                Answer Key
              </button>
            )}
            <button onClick={handleRefresh} className="btn btn-outline btn-small" title="Refresh">
              Refresh
            </button>
            <button 
              className="btn btn-outline"
              onClick={() => navigate('/admin')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        
        {selectedResponse ? (
          <div className="response-details" key={`response-${selectedResponse.id}`}>
            <div className="card">
              <div className="card-header">
                <h2>Response Details</h2>
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-primary"
                    onClick={async () => {
                      const pdfOptions = await getPdfOptionsWithPngLogo(400, 100);
                      exportResponseToPDF(quiz, selectedResponse, pdfOptions);
                    }}
                  >
                    Export to PDF
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={closeResponseDetails}
                  >
                    Back to Responses
                  </button>
                </div>
              </div>
              
              <div className="response-info">
                <div className="response-info-grid">
                  <div className="info-item">
                    <span className="info-label">Participant</span>
                    <span className="info-value">{selectedResponse.participant_name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{quiz.quiz_type === 'evaluation' ? 'Trainee' : 'Email'}</span>
                    <span className="info-value">
                      {quiz.quiz_type === 'evaluation' 
                        ? (selectedResponse.trainee_name || 'N/A')
                        : (selectedResponse.participant_email || 'N/A')}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Started</span>
                    <span className="info-value">{formatDate(selectedResponse.started_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Completed</span>
                    <span className="info-value">{formatDate(selectedResponse.completed_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Duration</span>
                    <span className="info-value">
                      {calculateDuration(selectedResponse.started_at, selectedResponse.completed_at)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Scoring Information - Only show for quizzes, not evaluations */}
              {quiz.quiz_type !== 'evaluation' && selectedResponse.scoring && (
                <div className="scoring-info">
                  <h3>Score Summary</h3>
                  <div className="score-summary-grid">
                    <div className="score-item overall-score">
                      <span className="score-label">Overall Score</span>
                      <span className="score-value" style={{
                        color: selectedResponse.scoring.score_percentage !== null && selectedResponse.scoring.score_percentage >= 70 ? '#4CAF50' : 
                               selectedResponse.scoring.score_percentage !== null && selectedResponse.scoring.score_percentage >= 50 ? '#FF9800' : '#F44336'
                      }}>
                        {selectedResponse.scoring.score_percentage !== null ? `${selectedResponse.scoring.score_percentage}%` : 'N/A'}
                      </span>
                    </div>
                    <div 
                      className="score-item"
                      onClick={() => setShowScoreBreakdown(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="score-label">Score</span>
                      <span className="score-value">{selectedResponse.scoring.score_display}</span>
                    </div>
                    <div 
                      className={`score-item correct ${answerFilter === 'correct' ? 'active' : ''}`}
                      onClick={() => setAnswerFilter('correct')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="score-label">Correct</span>
                      <span className="score-value">{selectedResponse.scoring.correct_count ?? Math.round(selectedResponse.scoring.correct_answers)}</span>
                    </div>
                    <div 
                      className={`score-item partial ${answerFilter === 'partial' ? 'active' : ''}`}
                      onClick={() => setAnswerFilter('partial')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="score-label">Partial</span>
                      <span className="score-value">{selectedResponse.scoring.partial_count ?? 0}</span>
                    </div>
                    <div 
                      className={`score-item incorrect ${answerFilter === 'incorrect' ? 'active' : ''}`}
                      onClick={() => setAnswerFilter('incorrect')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="score-label">Incorrect</span>
                      <span className="score-value">{selectedResponse.scoring.incorrect_count ?? Math.round(selectedResponse.scoring.incorrect_answers)}</span>
                    </div>
                    <div 
                      className={`score-item ${answerFilter === null ? 'active' : ''}`}
                      onClick={() => setAnswerFilter(null)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="score-label">Total</span>
                      <span className="score-value">{selectedResponse.scoring.total_questions}</span>
                    </div>
                  </div>
                  {selectedResponse.scoring.non_gradable_questions > 0 && (
                    <p className="non-gradable-note">
                      <em>Note: {selectedResponse.scoring.non_gradable_questions} non-gradable questions include audio responses and text questions without defined correct answers.</em>
                    </p>
                  )}
                </div>
              )}

              
              {selectedResponse.answers && selectedResponse.answers.length > 0 ? (
                <div className="answers-list">
                  {(() => {
                    const filteredAnswers = selectedResponse.answers.filter((answer) => {
                      if (answerFilter === null) return true;
                      
                      // Check rating first (manual rating takes precedence)
                      if (answer.rating) {
                        if (answerFilter === 'correct' && answer.rating === 3) return true;
                        if (answerFilter === 'partial' && answer.rating === 2) return true;
                        if (answerFilter === 'incorrect' && answer.rating === 1) return true;
                        return false;
                      }
                      
                      // Check is_correct for auto-graded answers
                      if (answer.is_gradable && answer.is_correct !== undefined) {
                        if (answerFilter === 'correct' && answer.is_correct === true) return true;
                        if (answerFilter === 'partial' && answer.is_correct === 'partial') return true;
                        if (answerFilter === 'incorrect' && answer.is_correct === false) return true;
                        return false;
                      }
                      
                      return false;
                    });
                    
                    return (
                      <>
                        <h3>
                          {quiz.quiz_type === 'evaluation' ? 'Evaluation Responses' : 'Answers'} ({filteredAnswers.length})
                        </h3>
                        
                        {filteredAnswers.map((answer) => {
                          const originalIndex = selectedResponse.answers?.findIndex(a => a.id === answer.id) ?? -1;
                          return (
                    <div 
                      className="card answer-item" 
                      key={answer.id}
                      style={{
                        borderLeft: answer.rating 
                          ? `4px solid ${answer.rating === 3 ? '#4CAF50' : answer.rating === 2 ? '#FF9800' : '#F44336'}`
                          : answer.is_gradable && answer.is_correct !== undefined
                          ? `4px solid ${answer.is_correct === true ? '#4CAF50' : answer.is_correct === 'partial' ? '#FF9800' : '#F44336'}`
                          : '4px solid #e0e0e0'
                      }}>
                      <div className="question-header">
                        <span className="question-number">Q{originalIndex + 1}</span>
                        <span className="question-type-badge">{answer.question_type}</span>
                        {/* Grading indicator - Only show for quizzes, not evaluations */}
                        {quiz.quiz_type !== 'evaluation' && answer.is_gradable && !answer.rating && (
                          <span className={`grading-badge ${answer.is_correct === true ? 'correct' : answer.is_correct === 'partial' ? 'partial' : 'incorrect'}`}>
                            {answer.is_correct === true ? 'Correct' : answer.is_correct === 'partial' ? 'Partial' : 'Incorrect'}
                          </span>
                        )}
                        {quiz.quiz_type !== 'evaluation' && answer.rating && (
                          <span className={`grading-badge ${answer.rating === 3 ? 'correct' : answer.rating === 2 ? 'partial' : 'incorrect'}`}>
                            {answer.rating === 3 ? 'Correct' : answer.rating === 2 ? 'Partial' : 'Wrong'}
                          </span>
                        )}
                        {quiz.quiz_type !== 'evaluation' && !answer.is_gradable && !answer.rating && (
                          <span className="grading-badge non-gradable">
                            Not Graded
                          </span>
                        )}
                      </div>
                      
                      <p className="question-text">
                        <strong>{answer.question_text}</strong>
                      </p>
                      
                      {/* Display question audio */}
                      {answer.question_audio_file && (
                        <div className="question-audio" style={{ marginBottom: '15px' }}>
                          <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#666' }}>
                            Question Audio:
                          </div>
                          <AudioPlayer 
                            src={`${API_URL}/uploads/${answer.question_audio_file}`} 
                            title="Question audio for context"
                          />
                        </div>
                      )}
                      
                      {/* Display question images */}
                      {(answer.question_image_file || (answer.question_images && answer.question_images.length > 0)) && (
                        <div className="question-images" style={{ marginBottom: '15px' }}>
                          {/* Single image (legacy) */}
                          {answer.question_image_file && (
                            <div style={{ marginBottom: '10px' }}>
                              <ImageViewer 
                                src={`${API_URL}/uploads/${answer.question_image_file}`} 
                                title="Question Image" 
                                maxHeight={300}
                              />
                            </div>
                          )}
                          
                          {/* Multiple images gallery */}
                          {answer.question_images && answer.question_images.length > 0 && (
                            <div className="question-images-gallery">
                              {answer.question_images.map((img, imgIndex) => (
                                <div key={imgIndex} style={{ marginBottom: '10px' }}>
                                  <ImageViewer 
                                    src={`${API_URL}/uploads/${img.image_file}`} 
                                    title={img.caption || `Question Image ${imgIndex + 1}`}
                                    maxHeight={300}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {answer.question_type === 'multiple_choice' && (
                        <div className="answer-content">
                          {answer.allow_multiple_answers ? (
                            <>
                              <p><strong>Selected Answers (Multiple Choice):</strong></p>
                              {answer.selected_options && answer.selected_options.length > 0 ? (
                                <>
                                  <div className="selected-options">
                                    {answer.selected_options.map((option, idx) => (
                                      <div 
                                        key={idx} 
                                        className={`answer-option ${option.is_correct ? 'correct-answer' : 'incorrect-answer'}`}
                                        style={{ marginBottom: '5px' }}
                                      >
                                        {option.is_correct ? '✅' : '❌'} {option.text}
                                      </div>
                                    ))}
                                  </div>
                                  {answer.score_percentage !== undefined && (
                                    <div className="score-info" style={{ marginTop: '10px', fontWeight: 'bold' }}>
                                      Score: {answer.score_percentage}%
                                      {answer.score_percentage === 100 && ' - Perfect!'}
                                      {answer.score_percentage > 0 && answer.score_percentage < 100 && ' - Partial Credit'}
                                      {answer.score_percentage === 0 && ' - Incorrect'}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="no-answer">No options selected</div>
                              )}
                            </>
                          ) : (
                            <>
                              <p><strong>Selected Answer:</strong></p>
                              <div className={`answer-option ${answer.is_correct ? 'correct-answer' : 'incorrect-answer'}`}>
                                {answer.is_correct ? '✅' : '❌'} {answer.selected_option_text || 'No answer selected'}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      
                      {answer.question_type === 'text' && (
                        <div className="answer-content">
                          <p><strong>Text Answer:</strong></p>
                          <div className="text-answer">
                            {answer.text_answer || 'No answer provided'}
                          </div>
                          
                          {/* Show grading for text questions with correct answers */}
                          {answer.requires_correct_answer && answer.correct_text_answer && (
                            <div className="text-answer-grading" style={{marginTop: '10px'}}>
                              <div className={`grading-result ${answer.text_answer_is_correct ? 'correct' : 'incorrect'}`}>
                                {answer.text_answer_is_correct ? (
                                  <span style={{color: '#4CAF50'}}>Correct Answer</span>
                                ) : (
                                  <span style={{color: '#F44336'}}>Incorrect Answer</span>
                                )}
                              </div>
                              <div className="correct-answer-display" style={{marginTop: '5px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                <strong>Expected Answer:</strong> <em>{answer.correct_text_answer}</em>
                              </div>
                            </div>
                          )}
                          
                          {/* Show info for text questions without correct answers - Hide for evaluations */}
                          {!answer.requires_correct_answer && quiz?.quiz_type !== 'evaluation' && (
                            <div className="text-answer-info" style={{marginTop: '10px', color: '#666', fontSize: '0.9em'}}>
                              <em>This is an open-ended question (no specific correct answer defined)</em>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {answer.question_type === 'audio_response' && answer.audio_answer && (
                        <div className="answer-content">
                          <p><strong>Audio Answer:</strong></p>
                          <AudioPlayer 
                            src={`${API_URL}/uploads/${answer.audio_answer}`} 
                            title="Participant's audio response"
                          />
                        </div>
                      )}
                      
                      {/* Rating UI for non-gradable questions - Only show for quizzes, not evaluations */}
                      {quiz.quiz_type !== 'evaluation' && ((answer.question_type === 'text' && !answer.requires_correct_answer) || answer.question_type === 'audio_response') ? (
                        <div className="rating-section" style={{marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                            <h4 style={{margin: 0}}>Rate this answer:</h4>
                            {answer.rating && !editingAnswers[answer.id] ? (
                              <button
                                className="btn btn-outline btn-small"
                                onClick={() => setEditingAnswers(prev => ({ ...prev, [answer.id]: true }))}
                              >
                              Edit Evaluation
                              </button>
                            ) : null}
                          </div>
                          {!answer.rating || editingAnswers[answer.id] ? (
                          <>
                          {/* AI Suggestion Section */}
                          {answer.question_type === 'text' && answer.text_answer && (
                            <div className="ai-suggestion-section" style={{marginBottom: '15px', display: 'none'}}>
                              {!aiSuggestions[answer.id] && !loadingAiSuggestion[answer.id] && (
                                <button
                                  className="btn btn-outline btn-small"
                                  onClick={() => fetchAiSuggestion(answer.id)}
                                  style={{
                                    backgroundColor: '#E3F2FD',
                                    borderColor: '#2196F3',
                                    color: '#1976D2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  🤖 Get AI Suggestion
                                </button>
                              )}
                              
                              {loadingAiSuggestion[answer.id] && (
                                <div style={{
                                  padding: '10px',
                                  backgroundColor: '#E3F2FD',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px'
                                }}>
                                  <span className="loading-spinner" style={{width: '16px', height: '16px'}}></span>
                                  <span>Analyzing answer...</span>
                                </div>
                              )}
                              
                              {aiSuggestions[answer.id] && !aiSuggestions[answer.id].error && (
                                <div style={{
                                  padding: '12px',
                                  backgroundColor: '#E3F2FD',
                                  border: '1px solid #2196F3',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                                    <span style={{fontWeight: 'bold', color: '#1976D2'}}>🤖 AI Suggestion</span>
                                    <span style={{
                                      fontSize: '12px',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      backgroundColor: aiSuggestions[answer.id].confidence === 'high' ? '#4CAF50' : 
                                                       aiSuggestions[answer.id].confidence === 'medium' ? '#FF9800' : '#F44336',
                                      color: 'white'
                                    }}>
                                      {aiSuggestions[answer.id].confidence} confidence
                                    </span>
                                  </div>
                                  
                                  <div style={{marginBottom: '10px'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px'}}>
                                      <span>Similarity:</span>
                                      <div style={{
                                        flex: 1,
                                        height: '8px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        overflow: 'hidden'
                                      }}>
                                        <div style={{
                                          width: `${aiSuggestions[answer.id].similarity_percentage}%`,
                                          height: '100%',
                                          backgroundColor: aiSuggestions[answer.id].similarity_percentage >= 65 ? '#4CAF50' :
                                                          aiSuggestions[answer.id].similarity_percentage >= 35 ? '#FF9800' : '#F44336',
                                          transition: 'width 0.3s ease'
                                        }}></div>
                                      </div>
                                      <span style={{fontWeight: 'bold'}}>{aiSuggestions[answer.id].similarity_percentage}%</span>
                                    </div>
                                    <div style={{
                                      padding: '8px',
                                      backgroundColor: aiSuggestions[answer.id].suggested_rating === 3 ? '#E8F5E9' :
                                                       aiSuggestions[answer.id].suggested_rating === 2 ? '#FFF3E0' : '#FFEBEE',
                                      borderRadius: '4px',
                                      textAlign: 'center',
                                      fontWeight: 'bold'
                                    }}>
                                      Suggested: {aiSuggestions[answer.id].rating_label}
                                    </div>
                                  </div>
                                  
                                  <div style={{display: 'flex', gap: '10px'}}>
                                    <button
                                      className="btn btn-primary btn-small"
                                      onClick={() => applyAiSuggestion(answer.id)}
                                      style={{flex: 1}}
                                    >
                                      ✓ Apply Suggestion
                                    </button>
                                    <button
                                      className="btn btn-outline btn-small"
                                      onClick={() => setAiSuggestions(prev => {
                                        const newState = {...prev};
                                        delete newState[answer.id];
                                        return newState;
                                      })}
                                      style={{flex: 1}}
                                    >
                                      ✗ Dismiss
                                    </button>
                                  </div>
                                </div>
                              )}
                              
                              {aiSuggestions[answer.id]?.error && (
                                <div style={{
                                  padding: '10px',
                                  backgroundColor: '#FFEBEE',
                                  border: '1px solid #F44336',
                                  borderRadius: '6px',
                                  color: '#C62828',
                                  fontSize: '14px'
                                }}>
                                  ⚠️ {aiSuggestions[answer.id].error}
                                  <button
                                    className="btn btn-outline btn-small"
                                    onClick={() => {
                                      setAiSuggestions(prev => {
                                        const newState = {...prev};
                                        delete newState[answer.id];
                                        return newState;
                                      });
                                      fetchAiSuggestion(answer.id);
                                    }}
                                    style={{marginLeft: '10px'}}
                                  >
                                    Retry
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="rating-buttons" style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
                            <button
                              className={`btn ${answer.rating === 3 || (!answer.rating && ratingState[answer.id]?.rating === 3) ? 'btn-success' : 'btn-outline'}`}
                              onClick={() => {
                                const comment = ratingState[answer.id]?.comment || answer.admin_comment || '';
                                const mlEvalId = aiSuggestions[answer.id]?.ml_evaluation_id;
                                const wasAiAccepted = mlEvalId ? (aiSuggestions[answer.id]?.suggested_rating === 3) : undefined;
                                setRatingState({...ratingState, [answer.id]: {...(ratingState[answer.id] || {comment}), rating: 3}});
                                updateAnswerRating(answer.id, 3, comment, mlEvalId, wasAiAccepted);
                              }}
                              style={{flex: 1}}
                            >
                              ✅ Correct
                            </button>
                            <button
                              className={`btn ${answer.rating === 2 || (!answer.rating && ratingState[answer.id]?.rating === 2) ? 'btn-warning' : 'btn-outline'}`}
                              onClick={() => {
                                const comment = ratingState[answer.id]?.comment || answer.admin_comment || '';
                                const mlEvalId = aiSuggestions[answer.id]?.ml_evaluation_id;
                                const wasAiAccepted = mlEvalId ? (aiSuggestions[answer.id]?.suggested_rating === 2) : undefined;
                                setRatingState({...ratingState, [answer.id]: {...(ratingState[answer.id] || {comment}), rating: 2}});
                                updateAnswerRating(answer.id, 2, comment, mlEvalId, wasAiAccepted);
                              }}
                              style={{flex: 1}}
                            >
                              🟡 Partially Correct
                            </button>
                            <button
                              className={`btn ${answer.rating === 1 || (!answer.rating && ratingState[answer.id]?.rating === 1) ? 'btn-danger' : 'btn-outline'}`}
                              onClick={() => {
                                const comment = ratingState[answer.id]?.comment || answer.admin_comment || '';
                                const mlEvalId = aiSuggestions[answer.id]?.ml_evaluation_id;
                                const wasAiAccepted = mlEvalId ? (aiSuggestions[answer.id]?.suggested_rating === 1) : undefined;
                                setRatingState({...ratingState, [answer.id]: {...(ratingState[answer.id] || {comment}), rating: 1}});
                                updateAnswerRating(answer.id, 1, comment, mlEvalId, wasAiAccepted);
                              }}
                              style={{flex: 1}}
                            >
                              ❌ Wrong
                            </button>
                          </div>
                          </>
                          ) : (
                            <div className="rating-display" style={{marginBottom: '10px'}}>
                              <div style={{
                                padding: '10px',
                                backgroundColor: answer.rating === 3 ? '#E8F5E9' : answer.rating === 2 ? '#FFF3E0' : '#FFEBEE',
                                borderRadius: '4px',
                                border: `2px solid ${answer.rating === 3 ? '#4CAF50' : answer.rating === 2 ? '#FF9800' : '#F44336'}`,
                                textAlign: 'center',
                                fontWeight: 'bold'
                              }}>
                                {answer.rating === 3 ? 'Correct' : answer.rating === 2 ? 'Partially Correct' : 'Wrong'}
                              </div>
                            </div>
                          )}
                          <div style={{marginTop: '10px'}}>
                            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold', position: 'relative'}}>
                              Comment:
                              {savingComments[answer.id] && (
                                <span style={{
                                  marginLeft: '10px',
                                  fontSize: '0.85em',
                                  color: '#666',
                                  fontStyle: 'italic',
                                  fontWeight: 'normal'
                                }}>
                                  Saving...
                                </span>
                              )}
                            </label>
                            {!answer.rating || editingAnswers[answer.id] ? (
                              <>
                                <textarea
                                  value={ratingState[answer.id]?.comment !== undefined ? ratingState[answer.id].comment : (answer.admin_comment || '')}
                                  onChange={(e) => {
                                    const newComment = e.target.value;
                                    setRatingState({...ratingState, [answer.id]: {...(ratingState[answer.id] || {}), comment: newComment}});
                                    // Auto-save comment after user stops typing
                                    getDebouncedSave(answer.id)(newComment);
                                  }}
                                  placeholder="Add a comment about this answer..."
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: savingComments[answer.id] ? '1px solid #4CAF50' : '1px solid #ddd',
                                    minHeight: '60px',
                                    transition: 'border-color 0.3s ease'
                                  }}
                                />
                                <div style={{
                                  fontSize: '0.85em',
                                  color: '#666',
                                  marginTop: '5px',
                                  fontStyle: 'italic'
                                }}>
                                  💡 Comments auto-save as you type
                                </div>
                              </>
                            ) : (
                              <div style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                minHeight: '60px',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {answer.admin_comment || <em style={{color: '#999'}}>No comment</em>}
                              </div>
                            )}
                          </div>
                          {(answer.rated_at || answer.rated_by) && (
                            <p style={{fontSize: '0.85em', color: '#666', marginTop: '10px'}}>
                              Rated{answer.rated_by ? ` by ${answer.rated_by}` : ''}{answer.rated_at ? ` on ${formatDate(answer.rated_at)}` : ''}
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="empty-state">
                  <h3>📭 No Answers</h3>
                  <p>No answers available for this response.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="card mb-3">
              <div className="card-header">
                <h2>Response Overview</h2>
                {/* <div className="d-flex gap-2">
                  {responses.length > 0 && (
                    <button onClick={exportResponses} className="btn btn-outline btn-small">
                      Export CSV
                    </button>
                  )}
                </div> */}
              </div>
              
              <div className="d-flex gap-3 flex-wrap">
                <div className="stat-card">
                  <div className="stat-number">{totalResponses}</div>
                  <div className="stat-label">Total Responses</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">
                    {responses.filter(r => r.completed_at).length}
                  </div>
                  <div className="stat-label">Completed</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">
                    {responses.filter(r => !r.completed_at).length}
                  </div>
                  <div className="stat-label">In Progress</div>
                </div>
              </div>
            </div>

            {responses.length === 0 ? (
              <div className="card empty-state">
                <h2>No Responses Yet</h2>
                <p>No one has taken this quiz yet. Share the quiz link to start collecting responses.</p>
                <div className="share-link mt-3">
                  <input
                    type="text"
                    value={`${window.location.origin}/quiz/${id}`}
                    readOnly
                    className="form-control"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/quiz/${id}`);
                      showToast('Quiz link copied to clipboard!', 'success');
                    }}
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="d-flex justify-content-between align-items-center mb-3" style={{ flexWrap: 'wrap', gap: '15px' }}>
                  <h2 style={{ margin: 0 }}>
                    All Responses ({totalResponses}{searchQuery ? ` matching "${searchQuery}"` : ''})
                  </h2>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', minWidth: '250px', flex: '1 1 250px' }}>
                      <input
                        type="text"
                        placeholder={`Search by ${quiz.quiz_type === 'evaluation' ? 'trainer, trainee, or ID' : 'name, email, or ID'}`}
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        style={{
                          width: '100%',
                          padding: '8px 12px 8px 40px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                      />
                      <svg
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '16px',
                          height: '16px',
                          color: '#666',
                          pointerEvents: 'none'
                        }}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                      {searchQuery && (
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setCurrentPage(1);
                            fetchData(1, '');
                          }}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#999',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Clear search"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {responses.length === 0 ? (
                  <div style={{ 
                    padding: '40px 20px', 
                    textAlign: 'center', 
                    color: '#666',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '6px'
                  }}>
                    <p style={{ margin: 0, fontSize: '16px', marginBottom: '15px' }}>
                      {searchQuery ? `No responses found matching "${searchQuery}"` : 'No responses found'}
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setCurrentPage(1);
                          fetchData(1, '');
                        }}
                        className="btn btn-outline btn-small"
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="response-list">
                      <div className="table-responsive">
                        <table className="response-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>{quiz.quiz_type === 'evaluation' ? 'Trainer' : 'Name'}</th>
                              <th>{quiz.quiz_type === 'evaluation' ? 'Trainee' : 'Email'}</th>
                              <th>Started</th>
                              <th>Completed</th>
                              <th>Duration</th>
                              {quiz.quiz_type !== 'evaluation' && <th>Score</th>}
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {responses.map(response => (
                          <tr key={response.id}>
                            <td>#{response.id}</td>
                            <td>
                              <strong>{response.participant_name}</strong>
                            </td>
                            <td>
                              {quiz.quiz_type === 'evaluation' ? (
                                <strong>{response.trainee_name || 'N/A'}</strong>
                              ) : (
                                <a href={`mailto:${response.participant_email}`}>
                                  {response.participant_email}
                                </a>
                              )}
                            </td>
                            <td>{formatDate(response.started_at)}</td>
                            <td>{formatDate(response.completed_at)}</td>
                            <td>
                              {calculateDuration(response.started_at, response.completed_at)}
                            </td>
                            {quiz.quiz_type !== 'evaluation' && (
                              <td>
                                {response.completed_at && response.scoring ? (
                                  <div className="score-display">
                                    <div 
                                      className="score-percentage"
                                      style={{
                                        color: response.scoring.score_percentage !== null && response.scoring.score_percentage >= 70 ? '#4CAF50' : 
                                               response.scoring.score_percentage !== null && response.scoring.score_percentage >= 50 ? '#FF9800' : '#F44336',
                                        fontWeight: 'bold',
                                        fontSize: '1.1em'
                                      }}
                                    >
                                      {response.scoring.score_percentage !== null ? `${response.scoring.score_percentage}%` : '0%'}
                                    </div>
                                    <div style={{ fontSize: '0.75em', color: '#666', marginTop: '2px' }}>
                                      {response.scoring.score_display.replace(/\s*\(\d+%\)/, '')}
                                    </div>
                                  </div>
                                ) : (
                                  <span style={{ color: '#999' }}>—</span>
                                )}
                              </td>
                            )}
                            <td>
                              {response.completed_at ? (
                                <span className="status-completed">Completed</span>
                              ) : (
                                <span className="status-in-progress">In Progress</span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-primary btn-small"
                                onClick={() => viewResponseDetails(response.id, response.participant_name)}
                                disabled={loadingDetails}
                              >
                                {loadingDetails ? 'Loading...' : 'View Details'}
                              </button>
                            </td>
                          </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '6px',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ color: '#666', fontSize: '14px' }}>
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResponses)} of {totalResponses} responses
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-outline btn-small"
                            onClick={firstPage}
                            disabled={currentPage === 1 || loading}
                            title="First page"
                          >
                            ««
                          </button>
                          <button
                            className="btn btn-outline btn-small"
                            onClick={prevPage}
                            disabled={currentPage === 1 || loading}
                            title="Previous page"
                          >
                            ‹
                          </button>
                          
                          {/* Page numbers */}
                          <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum: number;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNum}
                                  className={`btn btn-small ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                                  onClick={() => goToPage(pageNum)}
                                  disabled={loading}
                                  style={{
                                    minWidth: '40px'
                                  }}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          
                          <button
                            className="btn btn-outline btn-small"
                            onClick={nextPage}
                            disabled={currentPage === totalPages || loading}
                            title="Next page"
                          >
                            ›
                          </button>
                          <button
                            className="btn btn-outline btn-small"
                            onClick={lastPage}
                            disabled={currentPage === totalPages || loading}
                            title="Last page"
                          >
                            »»
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ color: '#666', fontSize: '14px' }}>
                            Page {currentPage} of {totalPages}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Score Breakdown Dialog - Rendered at page level for proper centering */}
      {showScoreBreakdown && selectedResponse?.scoring && (
        <div className="modal-overlay" onClick={() => setShowScoreBreakdown(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ paddingBottom: '8px', marginBottom: '12px' }}>
              <h3 className="modal-title" style={{ fontSize: '18px', margin: 0 }}>Score Breakdown</h3>
              <button 
                className="modal-close"
                onClick={() => setShowScoreBreakdown(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 16px 12px' }}>
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  color: selectedResponse.scoring.score_percentage !== null && selectedResponse.scoring.score_percentage >= 70 ? '#4CAF50' : 
                         selectedResponse.scoring.score_percentage !== null && selectedResponse.scoring.score_percentage >= 50 ? '#FF9800' : '#F44336',
                  marginBottom: '4px'
                }}>
                  {selectedResponse.scoring.score_display}
                </div>
                {selectedResponse.scoring.score_percentage !== null && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {selectedResponse.scoring.score_percentage}% correct
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#333' }}>Breakdown</h4>
                <div style={{ display: 'grid', gap: '4px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span><strong>Total Questions:</strong></span>
                    <span>{selectedResponse.scoring.total_questions}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span><strong>Gradable:</strong></span>
                    <span>{selectedResponse.scoring.gradable_questions}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#4CAF50' }}>
                    <span><strong>Correct:</strong></span>
                    <span>{selectedResponse.scoring.correct_count ?? Math.round(selectedResponse.scoring.correct_answers)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#FF9800' }}>
                    <span><strong>Partial:</strong></span>
                    <span>{selectedResponse.scoring.partial_count ?? 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#F44336' }}>
                    <span><strong>Incorrect:</strong></span>
                    <span>{selectedResponse.scoring.incorrect_count ?? Math.round(selectedResponse.scoring.incorrect_answers)}</span>
                  </div>
                  {selectedResponse.scoring.non_gradable_questions > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#666', fontSize: '12px' }}>
                      <span><strong>Non-Gradable:</strong></span>
                      <span>{selectedResponse.scoring.non_gradable_questions}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedResponse.scoring.gradable_questions > 0 && (
                <div style={{ padding: '10px', backgroundColor: '#E3F2FD', borderRadius: '6px', fontSize: '12px' }}>
                  <h4 style={{ marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#1976D2' }}>Calculation</h4>
                  <div style={{ lineHeight: '1.5', color: '#333' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Score Calculation:</strong>
                    </div>
                    <div style={{ marginLeft: '12px', marginBottom: '2px' }}>
                      • Correct: {selectedResponse.scoring.correct_count ?? Math.round(selectedResponse.scoring.correct_answers)} × 1.0 = {(selectedResponse.scoring.correct_count ?? Math.round(selectedResponse.scoring.correct_answers)) * 1.0} pts
                    </div>
                    {(selectedResponse.scoring.partial_count ?? 0) > 0 && (
                      <div style={{ marginLeft: '12px', marginBottom: '2px' }}>
                        • Partial: {selectedResponse.scoring.partial_count ?? 0} × 0.5 = {((selectedResponse.scoring.partial_count ?? 0) * 0.5).toFixed(1)} pts
                      </div>
                    )}
                    <div style={{ marginLeft: '12px', marginBottom: '2px' }}>
                      • Incorrect: {selectedResponse.scoring.incorrect_count ?? Math.round(selectedResponse.scoring.incorrect_answers)} × 0.0 = 0 pts
                    </div>
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #90CAF9', fontWeight: '600' }}>
                      <strong>Total:</strong> {(() => {
                        const correctPoints = (selectedResponse.scoring.correct_count ?? Math.round(selectedResponse.scoring.correct_answers)) * 1.0;
                        const partialPoints = (selectedResponse.scoring.partial_count ?? 0) * 0.5;
                        return (correctPoints + partialPoints).toFixed(1);
                      })()} / {selectedResponse.scoring.gradable_questions} = {selectedResponse.scoring.score_percentage !== null ? selectedResponse.scoring.score_percentage.toFixed(1) : '0'}%
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ paddingTop: '8px', marginTop: '8px' }}>
              <button 
                className="btn btn-primary btn-small"
                onClick={() => setShowScoreBreakdown(false)}
                style={{ fontSize: '13px', padding: '6px 16px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizResponsesPage; 